import {
	FormControlLabel,
	FormControlsWrapper,
	StyledIconButton,
} from '@Components/UI/Analytics/Analytics.styled';
import MultipleLinesChart from '@Components/UI/Charts/MultipleLinesChart/MultipleLinesChart';
import EdFormControl from '@Components/UI/Inputs/EdFormControl/EdFormControl';
import DateRangePickerPopup, {
	DateRangeOption,
} from '@Components/UI/Pickers/DateRangePickerPopup/DateRangePickerPopup';
import MonthRangePicker from '@Components/UI/Pickers/MonthRangePicker/MonthRangePicker';
import ReactHookDatePicker from '@Components/UI/Pickers/ReactHookDatePicker/ReactHookDatePicker';
import YearRangePicker from '@Components/UI/Pickers/YearRangePicker/YearRangePicker';
import { Button, Icon, MenuItem } from '@mui/material';
import { AnalyticsSlices } from '@Pages/ClassRooms/Constants/Classroom.constants';
import { useSnackbar } from '@Providers/useSnackbar';
import { GraphSlice, GraphSliceType } from '@Services/classrooms/classrooms.res.types';
import { FlexItem, FlexLayout, Spacer } from '@Styled/utilities';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { analyticsSeasonStart } from '../Constants/methods';
import { GraphSeries } from '../Types/Analytics.types';

type FormValues = { slice: GraphSliceType; dates: Date[] };

/**
 * The standard hub graph block: slice select + date presets popup feeding a
 * MultipleLinesChart. Owns its own form state — the graph range is
 * independent of the page tables, exactly like the existing analytics tabs.
 */
const MethodGraphSection: React.FC<{
	fetchSeries: (params: { slice: string; from: Date; to: Date }) => Promise<GraphSeries>;
	colors: (line: GraphSlice) => string;
	label?: string;
}> = ({ fetchSeries, colors, label = 'Enrollments over time' }) => {
	const { displaySnackbar } = useSnackbar();
	const [series, setSeries] = useState<GraphSeries>([]);
	const [dateRange, setDateRange] = useState<DateRangeOption>('custom');
	const [dateOpen, setDateOpen] = useState(false);
	const [dateAnchor, setDateAnchor] = useState<HTMLElement>();

	// Season default: from the most recent 1st of August until today.
	const defaults = useMemo<FormValues>(
		() => ({ slice: 'day', dates: [analyticsSeasonStart(), new Date()] }),
		[]
	);
	const { control, watch, setValue, reset, getValues } = useForm<FormValues>({
		defaultValues: defaults,
	});
	const selectedSlice = watch('slice');

	const load = useCallback(
		async (form?: FormValues) => {
			const values = form || getValues();
			try {
				const graph = await fetchSeries({
					slice: values.slice,
					from: values.dates[0],
					to: values.dates[1],
				});
				setSeries(graph || []);
			} catch (error: any) {
				displaySnackbar(
					'error',
					error.response?.data?.message || 'Unable to load the graph'
				);
			}
		},
		[displaySnackbar, fetchSeries, getValues]
	);

	useEffect(() => {
		load(defaults);
	}, [load, defaults]);

	const changeDateRange = (type: DateRangeOption) => {
		setDateRange(type);
		if (type === 'custom') return;
		const start = new Date();
		const end = new Date();
		if (type === 'last 2 weeks') start.setDate(start.getDate() - 14);
		if (type === 'last month') start.setMonth(start.getMonth() - 1);
		if (type === 'last 3 months') start.setMonth(start.getMonth() - 2);
		setValue('dates', [start, end]);
	};

	return (
		<>
			<FormControlsWrapper>
				<FormControlLabel>{label}</FormControlLabel>
				<Spacer mx={14} />
				<EdFormControl select name="slice" control={control}>
					{AnalyticsSlices.map((slice) => (
						<MenuItem key={slice.value} value={slice.value}>
							{slice.label}
						</MenuItem>
					))}
				</EdFormControl>
				<Spacer mx={14} />
				<StyledIconButton
					onClick={(event) => {
						setDateOpen(!dateOpen);
						setDateAnchor(event.currentTarget);
					}}
				>
					<Icon>event</Icon>
				</StyledIconButton>
				<DateRangePickerPopup
					open={dateOpen}
					anchorEl={dateAnchor}
					onClose={() => setDateOpen(false)}
					onChange={changeDateRange}
					rangeOptionValue={dateRange}
				>
					<DateRangePickerPopup.Body>
						{selectedSlice === 'month' && (
							<MonthRangePicker control={control as any} name="dates" />
						)}
						{selectedSlice === 'year' && (
							<YearRangePicker control={control as any} name="dates" />
						)}
						{selectedSlice !== 'month' && selectedSlice !== 'year' && (
							// Day/week slices had NO custom body at all — the popup
							// renders children only in custom mode, so custom was dead.
							<DayPickersRow>
								<div>
									<PickerLabel>From</PickerLabel>
									<ReactHookDatePicker control={control as any} name="dates.0" />
								</div>
								<div>
									<PickerLabel>To</PickerLabel>
									<ReactHookDatePicker control={control as any} name="dates.1" />
								</div>
							</DayPickersRow>
						)}
					</DateRangePickerPopup.Body>
				</DateRangePickerPopup>
				<FlexItem flex="1" />
				<FlexLayout>
					<Button
						onClick={() => {
							reset(defaults);
							setDateRange('custom');
							load(defaults);
						}}
						variant="outlined"
					>
						Reset
					</Button>
					<Spacer mx="1rem" />
					<Button onClick={() => load()} variant="contained">
						Apply
					</Button>
				</FlexLayout>
			</FormControlsWrapper>
			<MultipleLinesChart sliceType={selectedSlice} colors={colors} data={series as any} />
		</>
	);
};

const DayPickersRow = styled.div`
	display: flex;
	gap: 1rem;
	justify-content: center;
	margin-top: 1rem;
`;

const PickerLabel = styled.div`
	font-weight: 600;
	margin-bottom: 0.25rem;
	text-align: center;
`;

export default MethodGraphSection;
